import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/cn";

interface Props {
  label: string;
  value: ReactNode;
  hint?: string;
  icon?: ReactNode;
  tone?: "default" | "accent";
  to?: string;
  loading?: boolean;
  style?: CSSProperties;
}

export function StatCard({ label, value, hint, icon, tone = "default", to, loading, style }: Props) {
  const cardClass = cn(
    "animate-riseIn motion-reduce:animate-none bg-white border border-bridge-border rounded-lg p-5 flex items-start gap-4 min-h-[96px] transition-all hover:shadow-soft hover:translate-y-[-1px]",
    tone === "accent" && "ring-1 ring-bridge-accent/30",
    to && !loading && "cursor-pointer",
  );

  const content = (
    <>
      {icon && (
        <div className="h-10 w-10 rounded-md bg-bridge-accent text-white flex items-center justify-center shadow-sm shrink-0">
          {icon}
        </div>
      )}
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-wider font-bold text-bridge-muted/80">{label}</div>
        {loading ? (
          <div className="skeleton mt-1 h-7 w-16 rounded" />
        ) : (
          <div className="mt-1 text-xl font-bold text-bridge-fg tabular-nums">{value}</div>
        )}
        <div className="mt-1 text-[10px] text-bridge-subtle font-medium">{hint}</div>
      </div>
    </>
  );

  if (to) {
    return <Link to={to} className={cardClass} style={style}>{content}</Link>;
  }

  return <div className={cardClass} style={style}>{content}</div>;
}
